"use client";

import { useState } from "react";
import axios from "axios";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { SUBMIT_LEAD_API_URL } from "@/lib/rem-api";
import {
  EMPTY_FEEDBACK,
  FEEDBACK_ROLE_OPTIONS,
  FEEDBACK_TYPE_OPTIONS,
  type FeedbackFormState,
} from "@/lib/pillar-form-options";

/** Product feedback form with star rating. */
export function PillarFeedbackForm() {
  const [starRating, setStarRating] = useState(0);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackForm, setFeedbackForm] = useState<FeedbackFormState>(EMPTY_FEEDBACK);

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = feedbackForm.email.trim();
    const name = feedbackForm.name.trim();
    const message = feedbackForm.message.trim();

    if (!name) {
      setFeedbackError("Full name is required.");
      return;
    }
    if (!email) {
      setFeedbackError("Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFeedbackError("Please enter a valid email address.");
      return;
    }
    if (!message) {
      setFeedbackError("Message is required.");
      return;
    }

    setFeedbackLoading(true);
    setFeedbackError(null);

    try {
      const payload: Record<string, string | number> = {
        source: "website",
        contact_name: name,
        mobile: "",
        notes: `Feedback Type: ${feedbackForm.type || "General Feedback"}\nRole: ${feedbackForm.role || "Not specified"}\n\n${message}`,
        company_name: "",
        rating: starRating,
        email,
        phone: "",
        website: "",
        city: "",
        state: "",
        country: "",
        address: "",
        twitter: "",
        skype: "",
        facebook: "",
      };

      const response = await axios.post(SUBMIT_LEAD_API_URL, payload);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setFeedbackSubmitted(true);
      setFeedbackForm(EMPTY_FEEDBACK);
      setStarRating(0);
    } catch (err: unknown) {
      const messageText =
        err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setFeedbackError(messageText);
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <form className="feedback-form-wrap" onSubmit={handleFeedbackSubmit} noValidate>
      <h4>Share Your Thoughts Directly With the Team</h4>
      <p>
        Fill out the form below and your message goes directly to the RealtorsIn product team. No
        chatbots, no ticketing systems — real people, real responses, real improvements driven by real
        estate professionals like you.
      </p>
      <div className="fb-stars" id="fbStars">
        <span>Rate Your Experience:</span>
        {[1, 2, 3, 4, 5].map((val) => (
          <button
            key={val}
            type="button"
            className={`fb-star ${starRating >= val ? "active" : ""}`}
            onClick={() => setStarRating(val)}
          >
            ★
          </button>
        ))}
      </div>
      <div className="fb-form-grid">
        <div className="fb-form-field">
          <label htmlFor="fbName">Full Name</label>
          <input
            type="text"
            id="fbName"
            placeholder="Your name"
            value={feedbackForm.name}
            onChange={(e) => {
              setFeedbackForm((prev) => ({ ...prev, name: e.target.value }));
              if (feedbackError) setFeedbackError(null);
            }}
          />
        </div>
        <div className="fb-form-field">
          <label htmlFor="fbEmail">Email Address</label>
          <input
            type="email"
            id="fbEmail"
            placeholder="your@email.com"
            value={feedbackForm.email}
            onChange={(e) => {
              setFeedbackForm((prev) => ({ ...prev, email: e.target.value }));
              if (feedbackError) setFeedbackError(null);
            }}
          />
        </div>
        <div className="fb-form-field">
          <label htmlFor="fbRole">Your Role</label>
          <SearchableSelect
            id="fbRole"
            options={FEEDBACK_ROLE_OPTIONS}
            value={feedbackForm.role}
            onChange={(val) =>
              setFeedbackForm((prev) => ({ ...prev, role: val }))
            }
            placeholder="Select your role"
            searchable={false}
          />
        </div>
        <div className="fb-form-field">
          <label htmlFor="fbType">Feedback Type</label>
          <SearchableSelect
            id="fbType"
            options={FEEDBACK_TYPE_OPTIONS}
            value={feedbackForm.type}
            onChange={(val) =>
              setFeedbackForm((prev) => ({ ...prev, type: val }))
            }
            placeholder="Select type"
            searchable={false}
          />
        </div>
        <div className="fb-form-field full">
          <label htmlFor="fbMsg">Your Message</label>
          <textarea
            id="fbMsg"
            placeholder="Share your feedback, idea, or suggestion in as much detail as you like. The more specific you are, the better we can respond and act on it…"
            value={feedbackForm.message}
            onChange={(e) => {
              setFeedbackForm((prev) => ({ ...prev, message: e.target.value }));
              if (feedbackError) setFeedbackError(null);
            }}
          />
        </div>
      </div>
      <div className="fb-submit-row">
        {!feedbackSubmitted ? (
          <button className="btn-gold" type="submit" disabled={feedbackLoading}>
            {feedbackLoading ? "Sending..." : "📨 Submit Feedback"}
          </button>
        ) : (
          <div
            style={{
              padding: "14px 18px",
              background: "rgba(22,163,74,.1)",
              border: "1px solid rgba(22,163,74,.3)",
              borderRadius: "8px",
              color: "#16a34a",
              fontWeight: 700,
              fontSize: ".9rem",
            }}
          >
            ✅ Thank you! Your feedback has been received. The RealtorsIn team will review it and get
            back to you soon.
          </div>
        )}
        {feedbackError && (
          <p style={{ color: "#dc2626", fontSize: ".85rem", fontWeight: 600 }}>{feedbackError}</p>
        )}
        <p className="fb-note">
          Your feedback is confidential and used only to improve RealtorsIn.
          <br />
          We aim to respond to all submissions within 48 hours.
        </p>
      </div>
    </form>
  );
}
